import React from 'react'
import { preferredVoice, ThaiVoiceGender, VOICE_ORDER, voiceLabel } from '../../lib/sounds'
import SpeakerIcon from './speaker-icon'

type Props = {
   // The voices this word was recorded in. Empty renders a reserved, silent
   // slot rather than nothing, so whatever sits to its left stays in one column.
   voices: ThaiVoiceGender[]
   onPlay: (gender: ThaiVoiceGender) => void
   // Names the thing being spoken, for the controls' accessible labels.
   subject: string
   // Extra classes for the play glyph — the breakdown's component rows tint
   // their speaker to the part's palette slot.
   accentClass?: string
}

// The play target is the whole glyph plus its padding; the caret gets its own
// narrower one that bleeds into the row's right-hand gutter (the -8px below),
// so a thumb drifting off the edge still lands on the caret while a thumb aimed
// at the speaker never does. 8px, not more: on mobile the row already ends 8px
// from the viewport edge, and a deeper bleed would push the page sideways.
const PLAY_SIZE = 'h-[44px] w-[44px]'
const CARET_SIZE = 'h-[44px] w-[32px]'

// Reserved width for the pair, so rows with one voice, two voices or none all
// put their speaker in the same place.
const SLOT = 'w-[76px] -mr-[8px] shrink-0'

const ListenControl = ({ voices, onPlay, subject, accentClass }: Props) => {
   const [menuOpen, setMenuOpen] = React.useState(false)
   const wrapperRef = React.useRef<HTMLSpanElement>(null)
   const caretRef = React.useRef<HTMLButtonElement>(null)
   const menuRef = React.useRef<HTMLDivElement>(null)

   const lead = preferredVoice(voices)
   const hasChoice = voices.length > 1

   const closeMenu = React.useCallback((refocus: boolean) => {
      setMenuOpen(false)
      if (refocus) caretRef.current?.focus()
   }, [])

   React.useEffect(() => {
      if (!menuOpen) return

      const onPointerDown = (event: MouseEvent | TouchEvent) => {
         if (!wrapperRef.current?.contains(event.target as Node)) closeMenu(false)
      }
      const onKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape') closeMenu(true)
      }
      // Scrolling dismisses rather than repositions — the menu is a one-off
      // choice, and a reader who has moved on has already answered it.
      const onScroll = () => closeMenu(false)

      document.addEventListener('mousedown', onPointerDown)
      document.addEventListener('touchstart', onPointerDown)
      document.addEventListener('keydown', onKeyDown)
      window.addEventListener('scroll', onScroll, true)
      window.addEventListener('resize', onScroll)

      return () => {
         document.removeEventListener('mousedown', onPointerDown)
         document.removeEventListener('touchstart', onPointerDown)
         document.removeEventListener('keydown', onKeyDown)
         window.removeEventListener('scroll', onScroll, true)
         window.removeEventListener('resize', onScroll)
      }
   }, [menuOpen, closeMenu])

   // Keyboard users land inside the menu they just opened; mouse users don't
   // notice, since the first item is already the one the caret's speaker plays.
   React.useEffect(() => {
      if (!menuOpen) return
      menuRef.current?.querySelector('button')?.focus()
   }, [menuOpen])

   // No recording: hold the space open and say nothing. No icon, no
   // strikethrough, no apology.
   if (!lead) {
      return <span aria-hidden className={`${SLOT} block h-[44px]`} />
   }

   const play = (gender: ThaiVoiceGender) => {
      setMenuOpen(false)
      onPlay(gender)
   }

   // Lead first, whichever voice that is, so the list reads in the order the
   // control behaves: the item a keyboard user lands on is the one the speaker
   // beside it would have played. The rest keep the hero pill's order.
   const menuVoices = [lead, ...VOICE_ORDER.filter((g) => g !== lead && voices.includes(g))]

   return (
      <span
         ref={wrapperRef}
         className={`${SLOT} relative flex items-center justify-end ${menuOpen ? 'z-20' : ''}`}
      >
         <button
            type="button"
            className={`${PLAY_SIZE} inline-flex shrink-0 items-center justify-center rounded-full border-none bg-transparent p-0 text-speakerIdle cursor-pointer transition-all duration-150 hover:text-lightBlue hover:bg-lightBlueTransparent focus-visible:text-lightBlue focus-visible:bg-lightBlueTransparent ${
               // With only one voice there's no caret to sit beside, so the
               // speaker takes the caret's width as margin and stays put.
               hasChoice ? '' : 'mr-[32px]'
            } ${accentClass ?? ''}`.trim()}
            title={`Listen — ${voiceLabel(lead).toLowerCase()} voice`}
            aria-label={`Listen to ${subject} — ${voiceLabel(lead).toLowerCase()} voice`}
            onClick={() => play(lead)}
         >
            <SpeakerIcon className="text-[21px] block" />
         </button>

         {hasChoice && (
            <button
               ref={caretRef}
               type="button"
               aria-haspopup="menu"
               aria-expanded={menuOpen}
               className={`${CARET_SIZE} inline-flex shrink-0 items-center justify-start rounded-full border-none bg-transparent p-0 pl-[2px] cursor-pointer transition-all duration-150 [&_.material-icons]:text-[16px] ${
                  menuOpen
                     ? 'text-lightBlue bg-lightBlueTransparent'
                     : 'text-speakerIdle hover:text-lightBlue hover:bg-lightBlueTransparent focus-visible:text-lightBlue focus-visible:bg-lightBlueTransparent'
               }`}
               title="Choose a voice"
               aria-label={`Choose a voice for ${subject}`}
               onClick={() => setMenuOpen((open) => !open)}
            >
               <span className="material-icons">{menuOpen ? 'expand_less' : 'expand_more'}</span>
            </button>
         )}

         {/* An overlay anchored to the caret, not an expansion: nothing in the
             list moves, so the row you were reaching for stays where it was.
             Each item is itself a play button — picking a voice speaks it and
             closes. It's a one-off choice for this word, not a preference. */}
         {menuOpen && (
            <div
               ref={menuRef}
               role="menu"
               aria-label={`Voices for ${subject}`}
               className="absolute top-[46px] right-[-4px] w-[236px] rounded-[12px] border border-hairline bg-white p-[6px] shadow-[0_8px_24px_rgba(20,30,50,0.12),0_2px_6px_rgba(20,30,50,0.06)]"
            >
               <span
                  aria-hidden
                  className="absolute top-[-6px] right-[22px] h-[11px] w-[11px] rotate-45 border-l border-t border-l-hairline border-t-hairline bg-white"
               />
               {menuVoices.map((gender) => {
                  const isLead = gender === lead
                  return (
                     <button
                        key={gender}
                        type="button"
                        role="menuitem"
                        className={`relative flex h-[44px] w-full items-center gap-[10px] rounded-[8px] border-none px-[10px] text-left cursor-pointer ${
                           isLead ? 'bg-lightBlueTransparent' : 'bg-transparent hover:bg-chipBg'
                        }`}
                        onClick={() => play(gender)}
                     >
                        <SpeakerIcon
                           arcs={1}
                           className={`text-[19px] block shrink-0 ${isLead ? 'text-lightBlue' : 'text-speakerIdle'}`}
                        />
                        <span
                           className={`flex-1 text-[14.5px] text-bodyBlack ${isLead ? 'font-bold' : 'font-semibold'}`}
                        >
                           {voiceLabel(gender)} voice
                        </span>
                     </button>
                  )
               })}
            </div>
         )}
      </span>
   )
}

export default ListenControl
